const MIME_LABELS = {
    "image/png":       "PNG Image",
    "image/jpeg":      "JPEG Image",
    "image/gif":       "GIF Image",
    "image/webp":      "WebP Image",
    "image/svg+xml":   "SVG Vector",
    "video/mp4":       "MP4 Video",
    "video/webm":      "WebM Video",
    "audio/mpeg":      "MP3 Audio",
    "audio/wav":       "WAV Audio",
    "application/pdf": "PDF Document",
    "text/plain":      "Plain Text",
    "application/zip": "ZIP Archive",
};

export function mimeLabel(mime) {
    return MIME_LABELS[mime] ?? mime;
}

export function getTagLabel(tagId, tagMap) {
    // fe-search-1: a tag whose `name` is empty/whitespace/missing used to throw
    // (`label[0].toUpperCase()` on undefined), crashing the whole facet list
    // render. Fall back to the id when there's no usable name.
    const name = tagMap?.[tagId]?.name;
    if (typeof name === "string" && name.trim().length > 0) {
        return name[0].toUpperCase() + name.slice(1);
    }
    return tagId;
}

export function formatBytes(bytes) {
    if (bytes === undefined || bytes === null || bytes === "") return "–";
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let val = Number(bytes);
    while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
    return `${val.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatDuration(seconds) {
    if (seconds === undefined || seconds === null || seconds === "") return "–";
    const secNum = Number(seconds);
    const h = Math.floor(secNum / 3600);
    const m = Math.floor((secNum % 3600) / 60);
    const s = Math.floor(secNum % 60);

    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

/* ── Parsers ───────────────────────────────────────────────────────────────
 * The inverse of the two formatters above, so a range filter can show "1.4 MB"
 * and accept "1.4 MB" back. Before these the boxes were raw <input type=number>
 * over the stored unit, which meant filtering to 10 MB required typing
 * 10485760 — technically a control, practically not one.
 *
 * Both return null for anything unparseable so a caller can revert rather than
 * quietly filtering on NaN.
 * ========================================================================= */

const BYTE_UNITS = {
    "": 1, b: 1,
    k: 1024, kb: 1024,
    m: 1024 ** 2, mb: 1024 ** 2,
    g: 1024 ** 3, gb: 1024 ** 3,
    t: 1024 ** 4, tb: 1024 ** 4,
};

/** "10 MB" / "10mb" / "1.5 GB" / "500" (bare = bytes) → bytes. */
export function parseBytes(input) {
    if (typeof input === "number") return Number.isFinite(input) ? input : null;
    const raw = String(input ?? "").trim().toLowerCase().replace(/,/g, "");
    if (!raw) return null;
    // Accept the IEC spellings people actually type (KiB, MiB) as the same thing.
    const normalized = raw.replace(/i(b?)$/, "$1");
    const match = normalized.match(/^(\d*\.?\d+)\s*([a-z]*)$/);
    if (!match) return null;
    const value = Number(match[1]);
    const multiplier = BYTE_UNITS[match[2]];
    if (!Number.isFinite(value) || multiplier === undefined) return null;
    return Math.round(value * multiplier);
}

/** "1:30" / "1:02:03" / "90" / "90s" / "2m" / "1h" → seconds. */
export function parseDuration(input) {
    if (typeof input === "number") return Number.isFinite(input) ? input : null;
    const raw = String(input ?? "").trim().toLowerCase();
    if (!raw) return null;

    // Clock form — the shape formatDuration emits, so it round-trips.
    if (raw.includes(":")) {
        const parts = raw.split(":");
        if (parts.length > 3) return null;
        if (parts.some((p) => p !== "" && !/^\d*\.?\d+$/.test(p))) return null;
        const nums = parts.map((p) => (p === "" ? 0 : Number(p)));
        if (nums.some((n) => !Number.isFinite(n))) return null;
        return Math.round(nums.reduce((acc, n) => acc * 60 + n, 0));
    }

    const match = raw.match(/^(\d*\.?\d+)\s*([a-z]*)$/);
    if (!match) return null;
    const value = Number(match[1]);
    if (!Number.isFinite(value)) return null;

    const unit = match[2];
    if (unit === "") return Math.round(value);
    if (/^h(r|rs|our|ours)?$/.test(unit)) return Math.round(value * 3600);
    if (/^m(in|ins|inute|inutes)?$/.test(unit)) return Math.round(value * 60);
    if (/^s(ec|ecs|econd|econds)?$/.test(unit)) return Math.round(value);
    return null;
}